import { API_BASE_URL } from "../config/api";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const buildStatCards = (stats) => ([
  {
    title: "Total Groups",
    value: stats.totalGroups || 0,
    route: "/accounts/customer-groups",
    accent: "text-purple-700",
    bg: "from-purple-50 via-purple-100 to-white",
    helper: "Manage groups"
  },
  {
    title: "Total Customers",
    value: stats.totalCustomers || 0,
    route: "/accounts/customers",
    accent: "text-teal-700",
    bg: "from-teal-50 via-teal-100 to-white",
    helper: "View directory"
  },
  {
    title: "Open Complaints",
    value: stats.pendingComplaints || 0,
    route: "/accounts/complaints?status=pending",
    accent: "text-rose-700",
    bg: "from-rose-50 via-orange-50 to-white",
    helper: "Open details"
  },
  {
    title: "Closed Complaints",
    value: stats.completedComplaints || 0,
    route: "/accounts/complaints?status=completed",
    accent: "text-emerald-700",
    bg: "from-emerald-50 via-lime-50 to-white",
    helper: "Open details"
  },
  {
    title: "Reports Created",
    value: stats.reportsGenerated || 0,
    route: "/accounts/complaints",
    accent: "text-sky-700",
    bg: "from-sky-50 via-cyan-50 to-white",
    helper: "Open details"
  },
  {
    title: "Purchases",
    value: stats.purchaseCount || 0,
    route: "/accounts/material-stock/purchase",
    accent: "text-sky-700",
    bg: "from-sky-50 via-cyan-50 to-white",
    helper: "Open details"
  },
  {
    title: "Sales",
    value: stats.saleCount || 0,
    route: "/accounts/material-stock/sale",
    accent: "text-rose-700",
    bg: "from-rose-50 via-pink-50 to-white",
    helper: "Open details"
  },
  {
    title: "Orders",
    value: stats.orderCount || 0,
    route: "/accounts/material-stock/order",
    accent: "text-amber-700",
    bg: "from-amber-50 via-orange-50 to-white",
    helper: "Open details"
  }
]);

const quickLinks = [
  {
    title: "Customer Ledger View",
    description: "Open customer records and company details for follow-up coordination.",
    route: "/accounts/customers"
  },
  {
    title: "Complaint Register",
    description: "Check open tickets and make sure service requests are being processed.",
    route: "/accounts/complaints"
  },
  {
    title: "Suppliers",
    description: "Create suppliers, keep their contact details, and review purchase history.",
    route: "/accounts/dealers"
  },
  {
    title: "Material Stock",
    description: "Maintain stock in, stock out, vendor purchases, and customer supply entries.",
    route: "/accounts/material-stock"
  },
  {
    title: "Product Mapping",
    description: "Link vendor and customer part numbers to standard product master catalogs.",
    route: "/accounts/product-mapping"
  }
];

export default function AccountsDashboard() {
  const navigate = useNavigate();
  const accountsName = localStorage.getItem("accountsName") || "Accounts";
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/customer-groups");
        setGroups(res.data || []);
      } catch (err) {
        console.error("Failed to load groups on accounts dashboard", err);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_BASE_URL + "/api/dashboard", {
          params: { role: "accounts", groupId: selectedGroupId || undefined }
        });
        setStats(res.data || {});
      } catch (error) {
        console.error("Failed to load accounts dashboard", error);
        setStats({});
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, [selectedGroupId]);

  const statCards = useMemo(() => buildStatCards(stats), [stats]);
  const recentComplaints = Array.isArray(stats.recentComplaints) ? stats.recentComplaints : [];

  return (
    <>
      {/* ══ DESKTOP VIEW ══ */}
      <div className="hidden lg:block space-y-5 lg:space-y-8">
      <section className="theme-hero overflow-hidden rounded-[24px] border border-amber-200 bg-[radial-gradient(circle_at_top_left,_rgba(251,191,36,0.25),_transparent_35%),linear-gradient(135deg,_#fff7ed,_#ffffff,_#fef3c7)] p-3 shadow-sm sm:p-4 md:p-5 lg:rounded-[2rem] lg:p-8">
        <div className="grid gap-4 lg:gap-6 xl:grid-cols-[1.5fr,0.9fr] xl:items-end">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700 sm:text-xs lg:text-sm lg:tracking-[0.3em]">
                Accounts Desk
              </p>
              <h1 className="mt-2 text-xl font-bold text-stone-950 sm:text-2xl lg:mt-3 lg:text-3xl">
                Welcome back, {accountsName}
              </h1>
            </div>
            <div className="mt-3 md:mt-0">
              <select
                value={selectedGroupId}
                onChange={(e) => setSelectedGroupId(e.target.value)}
                className="h-10 rounded-lg border border-amber-200 bg-white px-4 text-sm text-stone-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 min-w-[200px]"
              >
                <option value="">All Customer Groups</option>
                {groups.map(g => (
                  <option key={g._id} value={g._id}>{g.groupName}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="theme-panel grid grid-cols-3 gap-2.5 rounded-2xl border border-white/70 bg-white/80 p-3 shadow-sm sm:gap-3 lg:rounded-[1.75rem] lg:gap-4 lg:p-5">
            <div>
              <p className="text-[9px] uppercase tracking-[0.16em] text-stone-500 sm:text-[10px] lg:text-xs lg:tracking-[0.25em]">Today Reports</p>
              <p className="mt-1.5 text-2xl font-bold leading-none text-stone-950 sm:text-3xl lg:mt-2">{loading ? "..." : stats.todayReports || 0}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.16em] text-stone-500 sm:text-[10px] lg:text-xs lg:tracking-[0.25em]">Total Customers</p>
              <p className="mt-1.5 text-2xl font-bold leading-none text-stone-950 sm:text-3xl lg:mt-2">{loading ? "..." : stats.totalCustomers || 0}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.16em] text-stone-500 sm:text-[10px] lg:text-xs lg:tracking-[0.25em]">Total Groups</p>
              <p className="mt-1.5 text-2xl font-bold leading-none text-stone-950 sm:text-3xl lg:mt-2">{loading ? "..." : stats.totalGroups || 0}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-2 gap-2.5 sm:gap-3 lg:gap-4 xl:grid-cols-4">
        {statCards.map((card) => (
          <button
            key={card.title}
            type="button"
            onClick={() => navigate(card.route)}
            className={`theme-stat-card rounded-2xl border border-white bg-gradient-to-br ${card.bg} p-3 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-lg sm:p-3.5 lg:rounded-[1.75rem] lg:p-6`}
          >
            <p className="theme-stat-label text-[9px] font-semibold uppercase tracking-[0.14em] text-stone-600 sm:text-[10px] lg:text-xs lg:tracking-[0.18em]">{card.title}</p>
            <p className={`mt-2 text-2xl font-bold leading-none ${card.accent} sm:text-3xl lg:mt-5 lg:text-4xl`}>
              {loading ? "..." : card.value}
            </p>
            <p className="theme-stat-helper mt-1.5 text-[10px] text-stone-500 lg:mt-4 lg:text-xs">{card.helper}</p>
          </button>
        ))}
      </section>

      <section className="grid gap-4 lg:gap-6 xl:grid-cols-[1.1fr,0.9fr]">
        <div className="theme-panel rounded-2xl border border-amber-100 bg-white p-4 shadow-sm lg:rounded-[2rem] lg:p-6">
          <div className="mb-4 lg:mb-5">
            <h2 className="text-lg font-bold text-stone-950 lg:text-xl">Quick Actions</h2>
          </div>

          <div className="grid gap-3 md:grid-cols-2 lg:gap-4">
            {quickLinks.map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => navigate(item.route)}
                className="rounded-2xl border border-stone-200 bg-stone-50 p-3.5 text-left transition hover:border-amber-300 hover:bg-amber-50 lg:rounded-[1.5rem] lg:p-5"
              >
                <h3 className="text-sm font-semibold text-stone-900 lg:text-base">{item.title}</h3>
                <p className="mt-1.5 text-xs leading-5 text-stone-600 lg:mt-2 lg:text-sm lg:leading-6">{item.description}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="theme-panel rounded-2xl border border-amber-100 bg-white p-4 shadow-sm lg:rounded-[2rem] lg:p-6">
          <div className="mb-4 flex items-center justify-between gap-3 lg:mb-5 lg:gap-4">
            <div>
              <h2 className="text-lg font-bold text-stone-950 lg:text-xl">Recent Complaints</h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/accounts/complaints")}
              className="rounded-full bg-stone-950 px-3 py-1.5 text-xs font-medium text-white lg:px-4 lg:py-2 lg:text-sm"
            >
              View All
            </button>
          </div>

          {recentComplaints.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-xs text-stone-500 lg:rounded-[1.5rem] lg:py-8 lg:text-sm">
              No recent complaints yet.
            </div>
          ) : (
            <div className="space-y-3">
              {recentComplaints.map((complaint) => (
                <button
                  key={complaint._id}
                  type="button"
                  onClick={() => navigate(`/accounts/complaints?complaintId=${complaint._id}`)}
                  className="w-full rounded-2xl border border-stone-200 px-3 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50 lg:rounded-[1.4rem] lg:px-4 lg:py-4"
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-stone-900 lg:text-base">{complaint.complaintId}</p>
                      <p className="text-xs text-stone-600 lg:text-sm">{complaint.company}</p>
                    </div>
                    <span className="inline-flex w-fit rounded-full bg-stone-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-stone-700 lg:px-3 lg:text-xs">
                      {complaint.status || "pending"}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-stone-500 lg:mt-3 lg:text-sm">
                    Assigned engineer: {complaint.engineerAssigned || "Not assigned"}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>
      </div>

      {/* ══ MOBILE VIEW — accounts premium ══ */}
      <div className="block lg:hidden">
        {/* Heading */}
        <div className="mb-4 mt-1 flex flex-col gap-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-600">Accounts Desk</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-stone-900">Welcome, {accountsName}</h1>
          </div>
          <div>
            <select
              value={selectedGroupId}
              onChange={(e) => setSelectedGroupId(e.target.value)}
              className="h-10 w-full rounded-lg border border-amber-200 bg-white px-4 text-sm text-stone-850 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100"
            >
              <option value="">All Customer Groups</option>
              {groups.map(g => (
                <option key={g._id} value={g._id}>{g.groupName}</option>
              ))}
            </select>
          </div>
        </div>

        {/* ── 6 metric cards in a grid ── */}
        <div className="mb-5 grid grid-cols-3 gap-2">
          {[
            { label: "Groups",    value: stats.totalGroups         || 0, to: "/accounts/customer-groups",             bg: "bg-purple-50",  num: "text-purple-700" },
            { label: "Customers", value: stats.totalCustomers      || 0, to: "/accounts/customers",                   bg: "bg-teal-50",    num: "text-teal-700" },
            { label: "Open",      value: stats.pendingComplaints   || 0, to: "/accounts/complaints?status=pending",   bg: "bg-amber-50",   num: "text-amber-700" },
            { label: "Closed",    value: stats.completedComplaints || 0, to: "/accounts/complaints?status=completed", bg: "bg-emerald-50", num: "text-emerald-700" },
            { label: "Purchase",  value: stats.purchaseCount       || 0, to: "/accounts/purchase-orders",             bg: "bg-rose-50",    num: "text-rose-700" },
            { label: "Sales",     value: stats.saleCount           || 0, to: "/accounts/sales-invoices",              bg: "bg-sky-50",     num: "text-sky-700" },
          ].map(card => (
            <button key={card.label} type="button" onClick={() => navigate(card.to)}
              className={`mobile-tap-highlight flex flex-col items-center gap-0.5 rounded-2xl ${card.bg} px-1 py-3 text-center shadow-sm active:scale-95 transition-transform`}>
              <span className={`text-[22px] font-black leading-none ${card.num}`}>
                {loading ? <span className="opacity-30 text-base">—</span> : card.value}
              </span>
              <span className="mt-0.5 text-[10px] font-semibold text-stone-500 leading-tight">{card.label}</span>
            </button>
          ))}
        </div>

        {/* ── Quick Create — 3 items + More ── */}
        <div className="mb-5">
          <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">Quick Create</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "P. Order", to: "/accounts/purchase-orders", color: "bg-amber-100",
                icon: <><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></> },
              { label: "Invoice",  to: "/accounts/sales-invoices",  color: "bg-emerald-100",
                icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></> },
              { label: "Stock",    to: "/accounts/material-stock",  color: "bg-sky-100",
                icon: <><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></> },
              { label: "Mapping",  to: "/accounts/product-mapping",  color: "bg-rose-100",
                icon: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></> }
            ].map(({ label, to, color, icon }) => (
              <button key={label} type="button" onClick={() => navigate(to)}
                className="mobile-tap-highlight flex flex-col items-center gap-1.5 active:scale-95 transition-transform">
                <div className={`flex h-14 w-14 items-center justify-center rounded-[18px] ${color} shadow-sm`}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-stone-700">{icon}</svg>
                </div>
                <span className="text-[11px] font-semibold text-stone-600">{label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Recent complaints */}
        {recentComplaints.length > 0 && (
          <div>
            <div className="mb-3 flex items-center justify-between">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">Recent Tickets</p>
              <button type="button" onClick={() => navigate("/accounts/complaints")} className="text-[11px] font-bold text-amber-600 mobile-tap-highlight">View all</button>
            </div>
            <div className="space-y-2.5">
              {recentComplaints.slice(0, 3).map(c => (
                <button key={c._id} type="button" onClick={() => navigate(`/accounts/complaints?complaintId=${c._id}`)}
                  className="mobile-tap-highlight flex w-full items-center gap-3.5 rounded-2xl border border-amber-100 bg-white px-4 py-3.5 text-left shadow-sm active:scale-[0.98] transition-transform">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-700">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-stone-900">{c.complaintId}</p>
                    <p className="mt-0.5 text-[11px] text-stone-400">{c.company || "—"}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-stone-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-stone-600">{c.status || "pending"}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}


